import {
    map,
    switchMap,
    distinctUntilChanged,
    debounceTime,
    tap,
} from 'rxjs/operators';
import { TranslatePipe } from '@ngx-translate/core';
import { Component, OnInit } from '@angular/core';
import * as moment from 'moment';
import { StateService } from '@uirouter/angular';
import { Subject, Observable } from 'rxjs';

import { Principal, FeatureFlagsService } from '../../../shared';
import { FormatDatePipe } from '../../_shared/format-date.pipe';
import {
    Session,
    SessionService,
    GraphQlService,
    CacheService,
} from '../../../_services';

@Component({
    selector: 'jhi-sessions',
    templateUrl: 'list.component.html',
    styleUrls: ['list.component.scss'],
    providers: [FormatDatePipe, TranslatePipe],
})
export class SessionListComponent implements OnInit {
    private static _date: Date = new Date();
    private static _displayMonth: string = moment(new Date()).format(
        'MMMM YYYY'
    );

    error: string;
    success: string;
    sessions: Session[];
    trainings: any = {};
    lastSessionsUpdate = 0;
    displayingMySessions = false;
    displayingDeletedSessions = false;
    loadingSessions = false;
    // Sorting
    title = 'desc';
    type = 'desc';
    local = 'desc';
    location = 'desc';
    trainer = 'desc';
    duration = 'desc';
    dateSort = 'desc';

    requestsToFetchData = new Subject();

    constructor(
        private sessionService: SessionService,
        private principal: Principal,
        private stateService: StateService,
        private graphQlService: GraphQlService,
        private cacheService: CacheService
    ) {
        this.displayingMySessions = stateService.is('sessions-list-mine');
        this.displayingDeletedSessions = stateService.is(
            'sessions-list-deleted'
        );
    }

    get date(): Date {
        return SessionListComponent._date;
    }

    set date(date: Date) {
        SessionListComponent._date = date;
    }

    get displayMonth(): string {
        return SessionListComponent._displayMonth;
    }

    set displayMonth(displayMonth: string) {
        SessionListComponent._displayMonth = displayMonth;
    }

    async ngOnInit() {
        this.sessions = this.cacheService.getResultFromCache(
            `session/list.component${
                this.displayingMySessions ? '-onlyOwn' : ''
            }`
        );
        // thanks to https://alligator.io/angular/real-time-search-angular-rxjs/
        this.requestsToFetchData
            .pipe(
                distinctUntilChanged(),
                tap(() => (this.loadingSessions = true)),
                switchMap(({ date, deleted = false, onlyOwn = false }) => {
                    if (onlyOwn) {
                        return this.getUserSessions();
                    }
                    const minStartDate = moment(this.date)
                        .startOf('month')
                        .toDate();
                    const maxStartDate = moment(this.date)
                        .endOf('month')
                        .toDate();
                    return this.getSessions(
                        minStartDate,
                        maxStartDate,
                        deleted
                    );
                })
            )
            .subscribe((sessions) => {
                this.sessions = sessions.map((session) =>
                    this.mapSession(session)
                );
                this.loadingSessions = false;
                this.cacheService.storeResult(
                    `session/list.component${
                        this.displayingMySessions ? '-onlyOwn' : ''
                    }`,
                    this.sessions
                );
            });
        const canListAllSessions = await this.principal.hasAnyAuthority([
            'ROLE_ADMIN',
            'ROLE_SALE',
            'ROLE_SESSION_PLANNER',
        ]);
        if (canListAllSessions && !this.displayingMySessions) {
            this.dateSort = 'asc';
            this.requestsToFetchData.next({
                date: this.date,
                deleted: this.displayingDeletedSessions,
            });
        } else {
            this.dateSort = 'desc';
            this.requestsToFetchData.next({
                date: this.date,
                deleted: this.displayingDeletedSessions,
                onlyOwn: true,
            });
        }
    }

    stringSortBy(column: string) {
        this[column] = this[column] === 'desc' ? 'asc' : 'desc';
        this.sessions = this.sessions.sort((sessionA, sessionB) => {
            if (
                sessionA[column] === null ||
                typeof sessionA[column] === 'undefined'
            ) {
                sessionA[column] = '';
            }
            if (
                sessionB[column] === null ||
                typeof sessionB[column] === 'undefined'
            ) {
                sessionB[column] = '';
            }
            if (this[column] === 'asc') {
                return sessionA[column]
                    .toLowerCase()
                    .localeCompare(sessionB[column].toLowerCase());
            } else {
                return sessionB[column]
                    .toLowerCase()
                    .localeCompare(sessionA[column].toLowerCase());
            }
        });
    }

    getFormattedStudentCount(session: Session) {
        const numberOfConfirmedStudents =
            session.numberOfConfirmedStudents || 0;
        const numberOfUnconfirmedStudents =
            session.numberOfUnconfirmedStudents || 0;
        if (numberOfUnconfirmedStudents === 0) {
            return numberOfConfirmedStudents;
        }
        const numberOfUnconfirmedStudentsLabel = `${numberOfUnconfirmedStudents} ${
            numberOfUnconfirmedStudents > 1 ? 'options' : 'option'
        }`;
        if (numberOfConfirmedStudents === 0) {
            return numberOfUnconfirmedStudentsLabel;
        }
        return `${numberOfConfirmedStudents} (+ ${numberOfUnconfirmedStudentsLabel})`;
    }

    numberSortBy(column: string) {
        this[column] = this[column] === 'desc' ? 'asc' : 'desc';
        this.sessions = this.sessions.sort((sessionA, sessionB) =>
            this[column] === 'asc'
                ? sessionA[column] - sessionB[column]
                : sessionB[column] - sessionA[column]
        );
    }

    dateSortBy() {
        this.dateSort = this.dateSort === 'desc' ? 'asc' : 'desc';
        this.sessions = this.sessions.sort((a, b) =>
            this.compareSessionsByStartDate(a, b)
        );
    }

    private compareSessionsByStartDate(a: Session, b: Session): number {
        if (this.dateSort === 'asc') {
            return this.getJSDate(a.startDate) - this.getJSDate(b.startDate);
        } else {
            return this.getJSDate(b.startDate) - this.getJSDate(a.startDate);
        }
    }

    updateDate(num, type): void {
        this.date = moment(this.date).add(num, type).toDate();
        this.displayMonth = moment(this.date).format('MMMM YYYY');
        this.sessions = [];
        this.requestsToFetchData.next({
            date: this.date,
            deleted: this.displayingDeletedSessions,
        });
    }

    mapSession(session): Session {
        let sessionTrainer = session.trainer
            ? session.trainer.firstName + ' ' + session.trainer.lastName
            : 'À définir';

        let sessionLocation = 'À définir';
        if (
            session.location &&
            (session.locationType === 'ZENIKA' ||
                session.locationType === 'PARTNER')
        ) {
            sessionLocation = session.location.name;
        } else if (session.company && session.locationType === 'COMPANY') {
            sessionLocation = session.company.name;
        } else if (session.locationType === 'REMOTE') {
            sessionLocation = 'À distance';
        }

        let sessionType =
            session.type === 'ONDEMAND'
                ? 'ondemand'
                : session.type === 'ONSITE'
                ? 'intra'
                : session.type === 'PUBLIC'
                ? 'inter'
                : 'autre';
        let sessionTrainerId;
        if (session.trainer) {
            sessionTrainerId = session.trainer.id;
        }

        return new Session(
            session.id,
            session.training.title,
            moment(session.startDate).toDate(),
            ' ',
            session.days,
            sessionTrainer,
            sessionTrainerId,
            sessionType,
            session.price,
            session.priceCurrency,
            session.training.reference,
            session.language,
            session.timezone,
            sessionLocation,
            session.locationType,
            session.local,
            session.room,
            Number.NaN,
            session.registrations,
            session.training,
            session.billing,
            session.deleted,
            session.trainingSessionExtended
                ? session.trainingSessionExtended.subject
                : '',
            session.trainingSessionExtended
                ? session.trainingSessionExtended.linkedTraining
                : null,
            null,
            session.trainingSessionExtended,
            session.additionalTrainers,
            null,
            session.confirmedRegistrations,
            session.pendingRegistrationOptions,
            session.hours,
            session.synchronizeSignature,
            session.timesheetSaved
        );
    }

    additionalTrainersList(session): string {
        return session.additionalTrainers
            .map((trainer) => `${trainer.firstName} ${trainer.lastName}`)
            .join(', ');
    }

    getUserSessions() {
        return this.sessionService.findAll();
    }

    getJSDate(date): any {
        return moment(date).toDate().getTime();
    }

    getSessions(
        minStartDate: Date,
        maxStartDate: Date,
        deleted: boolean
    ): Observable<any> {
        return this.graphQlService
            .post({
                query: `
                query(
                  $minStartDate: date!
                  $maxStartDate: date!
                  $deleted: Boolean = false
                ) {
                  trainingSessions: training_session_with_registration_counts(
                    where: {
                      _and: [
                        { start_date: { _gte: $minStartDate } }
                        { start_date: { _lte: $maxStartDate } }
                        { deleted: { _eq: $deleted } }
                      ]
                    }
                    order_by: [
                      { start_date: asc_nulls_first }
                      { training: { title: asc_nulls_first } }
                    ]
                  ) {
                    id
                    startDate: start_date
                    days
                    type
                    timesheetSaved: is_timesheet_saved
                    confirmedRegistrations: number_of_confirmed_students
                    pendingRegistrationOptions: number_of_unconfirmed_students
                    trainer {
                      id
                      firstName: first_name
                      lastName: last_name
                    }
                    training {
                      title
                    }
                    locationType: location_type
                    location {
                      name
                    }
                    company {
                      name
                    }
                    additionalTrainerParticipations: trainer_participations {
                      trainer {
                        firstName: first_name
                        lastName: last_name
                      }
                    }
                    trainingSessionExtended: extended {
                        subject
                        linkedTraining: linked_training {
                            title
                        }
                    }
                  }
                }
            `,
                variables: {
                    minStartDate,
                    maxStartDate,
                    deleted,
                },
            })
            .pipe(
                map(({ trainingSessions }) => {
                    return trainingSessions.map((session) => ({
                        ...session,
                        additionalTrainers:
                            session.additionalTrainerParticipations.map(
                                ({ trainer }) => trainer
                            ),
                    }));
                })
            );
    }

    export(): void {
        this.sessionService.exportPerMonth(
            moment(this.date).startOf('month').format('YYYY-MM-DD'),
            moment(this.date).endOf('month').format('YYYY-MM-DD'),
            this.displayingDeletedSessions
        );
    }
}



// WEBPACK FOOTER //
// ./src/app/_screens/session/list/list.component.ts